import angular from "angular";

import { AccountingData, Numeric, Language } from "@salaxy/core";
import { SettingsService } from "../../services";
import {
  Templates,
  AccountingMapper,
  ReportOptions,
  ReportData
} from "@salaxy/reports";

/**
 * Provides functionality to show report rows.
 */
export class AccountingReportRowsController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Templates", "$sce", "SettingsService"];

  /**
   * Report view table only (partial) or pdf preview (full)
   * Defaults to 'partial'
   */
  public view: "partial" | "full";

  /** Accounting data */
  public data: AccountingData;

  /** Boolean indicating if the refresh is in progress. */
  public refreshInprogress: boolean;

  /**Selected report language */
  public lang: string;

  /** Trusted language used in html */
  public calcLang: string;

  private renderedData: AccountingData;

  private reportOptions: ReportOptions;

  private renderedLang: string;

  private reportData: ReportData<AccountingData> = {
    headerFooter: {},
    layout: {},
    report: {},
  };

  private htmlData: any;

  constructor(
    private templates: Templates,
    private $sce: angular.ISCEService,
    private settingsService: SettingsService
  ) {
    // Dependency injection
  }

  /**
   * Initialize controller values.
   */
  public $onInit() {
    this.view = this.view || "partial";
    this.reportData.layout = AccountingMapper.getReportLayout(false);
  }

  /** Expand/close all nodes */
  public expand(close = false) {
    if (this.data) {
      for (const ledgerAccount of this.data.ledgerAccounts) {
        (ledgerAccount as any).rowToggle = !close;
      }
    }
  }

  /** Returns true if at least one account is toggled */
  public get anyExpanded(): boolean {
    if (this.data) {
      for (const ledgerAccount of this.data.ledgerAccounts) {
        if ((ledgerAccount as any).rowToggle) {
          return true;
        }
      }
    }
    return false;
  }

  /** Bindable and trusted html. */
  public get html(): any {
    this.calcLang = this.lang === "default" ? "fi" : this.lang;
    if (!this.data) {
      this.renderedData = null;
      this.renderedLang = null;
    } else {
      const data = angular.copy(this.data);

      if (!this.renderedData || !angular.equals(data, this.renderedData)
        || this.renderedLang !== this.calcLang) {
        this.renderedData = data;
        this.renderedLang = this.calcLang;
        this.refresh();
      }
    }
    return this.htmlData;
  }

  /**
   * Refresh calculation report data.
   */
  private refresh() {
    if (this.refreshInprogress) {
      return;
    }
    this.refreshInprogress = true;
    this.settingsService.getReportOptions().then((options) => {
      this.reportOptions = options;
      const reportOptions = this.getDefaultReportOptions(angular.copy(this.reportOptions));
      this.reportData.headerFooter = AccountingMapper.getHeaderFooter(this.data, this.reportOptions, this.calcLang as Language);
      const css = "";
      this.reportData.layout.hasCustomCss = false;
      this.reportData.layout.customCss = (css || "");
      this.reportData.layout.margin = {
        top: reportOptions.margin.top,
        right: reportOptions.margin.right,
        bottom: reportOptions.margin.bottom,
        left: reportOptions.margin.left
      };
      const templates = new Templates();
      this.reportData.headerHtml = this.reportOptions?.headerTemplate ? templates.getHtmlDynamic(this.reportOptions.headerTemplate, this.reportData.headerFooter, this.calcLang) : "";
      this.reportData.footerHtml = this.reportOptions?.headerTemplate ? templates.getHtmlDynamic(this.reportOptions.footerTemplate, this.reportData.headerFooter, this.calcLang) : "";
      this.reportData.report = AccountingMapper.getAccountingReport(this.data);
      this.htmlData = this.$sce.trustAsHtml(this.templates.getHtml("accountingReport", this.reportData, this.calcLang));
      this.refreshInprogress = false;
    });

  }

  private getDefaultReportOptions(optionsInput?: ReportOptions): ReportOptions {
    optionsInput = optionsInput || {};
    optionsInput.margin = optionsInput.margin || {};
    optionsInput.margin.top = Numeric.parseNumber(optionsInput.margin.top) || 30;
    optionsInput.margin.right = Numeric.parseNumber(optionsInput.margin.right) || 10;
    optionsInput.margin.bottom = Numeric.parseNumber(optionsInput.margin.bottom) || 40;
    optionsInput.margin.left = Numeric.parseNumber(optionsInput.margin.left) || 10;
    return optionsInput;
  }
}
